import { zodResolver } from "@hookform/resolvers/zod";
import { useQueryClient } from "@tanstack/react-query";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { toast } from "react-toastify";
import { twMerge } from "tailwind-merge";

import { CancelSubmitButtons, errorToast } from "~/common/components";
import Modal, { ModalProps } from "~/common/components/Modal";
import { FileUpload } from "~/common/components/ui/FileUpload";
import Input from "~/common/components/ui/Input";
import { fileSizeToBytes } from "~/common/constants";
import { consentDocumentsQueryOptionsFactory } from "~/providers/factories/consentDocumentsQueryOptionsFactory";
import { useCurrentTeamMemberContext } from "~/providers/hooks";
import { useUpdateConsentDocument } from "~/providers/hooks/useUpdateConsentDocument";
import { updateDocumentFormSchema } from "~/providers/schemas/consentDocuments";
import {
  ListConsentDocumentResponse,
  UpdateDocumentForm,
} from "~/providers/types/consentDocuments";

interface UpdateDocumentModalProps extends ModalProps {
  document: ListConsentDocumentResponse;
}

export const UpdateDocumentModal = ({
  document,
  onClose,
  show,
}: UpdateDocumentModalProps) => {
  const currentTeamMember = useCurrentTeamMemberContext();
  const updateDocumentMutation = useUpdateConsentDocument();
  const queryClient = useQueryClient();

  const {
    handleSubmit,
    control,
    register,
    formState: { errors, isValid, dirtyFields },
    watch,
  } = useForm<UpdateDocumentForm>({
    resolver: zodResolver(updateDocumentFormSchema),
    mode: "onChange",
    defaultValues: {
      name: document.name,
    },
  });

  const currentNameField = watch("name");

  const hasChanged =
    currentNameField.trim() !== document.name || dirtyFields.file;

  const organizationId =
    currentTeamMember.organization_team_members[0].organization_id;

  const onSubmit: SubmitHandler<UpdateDocumentForm> = ({ name, file }) => {
    updateDocumentMutation.mutate(
      {
        organizationId,
        consentId: document.id,
        name,
        file: file instanceof File ? file : undefined,
      },
      {
        onSuccess: () => {
          void queryClient.invalidateQueries({
            queryKey: consentDocumentsQueryOptionsFactory.list({
              organizationId,
            }).queryKey,
          });
          onModalClose();
          toast.success("Consent updated successfully");
        },
        onError: (error) => {
          errorToast(error);
        },
      },
    );
  };

  const onModalClose = () => {
    if (updateDocumentMutation.isPending) return;
    onClose();
  };

  return (
    <Modal
      title="Update document"
      show={show}
      onClose={onModalClose}
      className="xl:w-1/2"
    >
      <form
        noValidate
        onSubmit={handleSubmit(onSubmit)}
        className="flex flex-col gap-6"
      >
        <Input
          id="name"
          label="Name (patient facing)"
          required
          maxLength={100}
          charactersCount={currentNameField?.length}
          placeholder="e.g. Clinic terms and policies"
          error={errors.name?.message}
          {...register("name")}
        />

        <Controller
          control={control}
          name="file"
          render={({ field }) => {
            return (
              <FileUpload.Root
                accept={{ "application/pdf": [".pdf"] }}
                maxSize={fileSizeToBytes.fromMB(10)}
                initialFile={document}
                onFilesChange={(files) => {
                  field.onChange(files[0]);
                }}
                error={errors.file?.message}
              >
                <FileUpload.Dropzone
                  className={twMerge(errors.file && "border-red-400 bg-red-50")}
                >
                  <FileUpload.Content />
                </FileUpload.Dropzone>
                <FileUpload.List />
              </FileUpload.Root>
            );
          }}
        />

        <CancelSubmitButtons
          submitButtonLabel="Update document"
          handleCancel={onModalClose}
          isSubmitting={updateDocumentMutation.isPending}
          containerClassName="w-fit ml-auto"
          disableSubmit={
            !isValid || !hasChanged || updateDocumentMutation.isPending
          }
          disableCancel={updateDocumentMutation.isPending}
        />
      </form>
    </Modal>
  );
};
